/**
 * i18n ligero (sin dependencias): contexto + hook useT + persistencia.
 *
 * Añadir un idioma nuevo en el futuro:
 *   1. Crea dictionaries/<code>.json copiando las mismas claves.
 *   2. Impórtalo aquí y regístralo en LANGUAGES.
 * Todos los componentes que usan t() lo tomarán automáticamente.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import es from './dictionaries/es.json';
import en from './dictionaries/en.json';

// Registro de idiomas: code -> { label nativo, diccionario }.
export const LANGUAGES = {
  es: { label: 'Español', dict: es },
  en: { label: 'English', dict: en },
};

const DEFAULT_LANG = 'es';
const STORAGE_KEY = 'lang';

function readInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES[saved]) return saved;
    const nav = (navigator.language || '').slice(0, 2);
    if (LANGUAGES[nav]) return nav;
  } catch { /* ignore */ }
  return DEFAULT_LANG;
}

// Resuelve una clave anidada tipo "editor.bold" en el diccionario.
function lookup(dict, key) {
  return key.split('.').reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), dict);
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((code) => {
    if (!LANGUAGES[code]) return;
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  }, []);

  // t(key, fallback): devuelve la traducción; si falta, cae al español y luego
  // al fallback/clave (así una clave sin traducir nunca rompe la UI).
  const t = useCallback((key, fallback) => {
    const val = lookup(LANGUAGES[lang]?.dict, key);
    if (typeof val === 'string') return val;
    const esVal = lookup(LANGUAGES[DEFAULT_LANG].dict, key);
    if (typeof esVal === 'string') return esVal;
    return fallback != null ? fallback : key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback fuera del provider: t() devuelve la clave/fallback (no rompe).
    return { lang: DEFAULT_LANG, setLang: () => {}, t: (k, f) => (f != null ? f : k), languages: LANGUAGES };
  }
  return ctx;
}

/** Atajo: const t = useT(); t('editor.bold'). */
export function useT() {
  return useI18n().t;
}
